<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[10060] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      @keydown.escape.window="onEscape"
    >
      <div class="cliente-submodal bg-white dark:bg-gray-800 shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700 flex flex-col">
        <header class="cliente-submodal__header">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-md shrink-0">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">
              {{ isEdit ? t('workspaceClienteModal.contato.editTitle') : t('workspaceClienteModal.contato.addTitle') }}
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400">{{ t('workspaceClienteModal.contato.formSubtitle') }}</p>
          </div>
        </header>

        <div class="cliente-submodal__body space-y-3">
          <div>
            <span class="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{{ t('workspaceClienteModal.contato.tipo') }}</span>
            <div class="flex flex-wrap gap-2 mt-2">
              <button
                v-for="opt in tipoOptions"
                :key="opt.value"
                type="button"
                class="px-3 py-1.5 rounded-lg text-sm font-medium border transition-all inline-flex items-center gap-1.5"
                :class="local.tipo === opt.value
                  ? 'bg-sky-500 text-white border-sky-500'
                  : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200'"
                @click="onTipoChange(opt.value)"
              >
                <span v-html="opt.icon" />
                {{ opt.label }}
              </button>
            </div>
          </div>

          <ClienteFormField
            v-model="local.valor"
            :label="t('workspaceClienteModal.contato.valor')"
            required
            :type="valorInputType"
            :inputmode="valorInputMode"
            :error="fieldErrors.valor"
            @update:model-value="onValorInput"
          />

          <label class="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer">
            <input v-model="local.principal" type="checkbox" class="rounded border-gray-300 text-sky-600" />
            <span class="text-sm font-medium text-gray-800 dark:text-gray-200">{{ t('workspaceClienteModal.contato.principal') }}</span>
          </label>
        </div>

        <footer class="cliente-submodal__footer">
          <button type="button" class="btn-secondary" @click="$emit('close')">{{ t('common.cancel') }}</button>
          <button type="button" class="btn-primary" :disabled="!canSave" @click="save">{{ t('common.save') }}</button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ClienteFormField from './ClienteFormField.vue'
import '@/assets/styles/cliente-modal-theme.css'
import { maskPhoneBr } from '@/utils/inputMasks'

const props = defineProps({
  open: { type: Boolean, default: false },
  initial: { type: Object, default: null }
})

const emit = defineEmits(['close', 'save'])

const onEscape = (event) => {
  event.stopPropagation()
  emit('close')
}

const { t } = useI18n()

const empty = () => ({ tipo: 'EMAIL', valor: '', principal: false })

const local = ref(empty())
const fieldErrors = ref({ valor: '' })

const isEdit = computed(() => props.initial != null)

const tipoOptions = computed(() => [
  { value: 'EMAIL', label: 'Email', icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>' },
  { value: 'TELEFONE', label: t('workspaceClienteModal.contato.telefone'), icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>' },
  { value: 'WHATSAPP', label: 'WhatsApp', icon: '<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/></svg>' }
])

const valorInputType = computed(() => (local.value.tipo === 'EMAIL' ? 'email' : 'tel'))
const valorInputMode = computed(() => (local.value.tipo === 'EMAIL' ? 'email' : 'tel'))

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const validate = () => {
  const v = (local.value.valor || '').trim()
  if (!v) {
    fieldErrors.value.valor = t('workspaceClienteModal.contato.validation.required')
    return false
  }
  if (local.value.tipo === 'EMAIL' && !emailRegex.test(v)) {
    fieldErrors.value.valor = t('workspaceClienteModal.contato.validation.email')
    return false
  }
  if ((local.value.tipo === 'TELEFONE' || local.value.tipo === 'WHATSAPP') && v.replace(/\D/g, '').length < 10) {
    fieldErrors.value.valor = t('workspaceClienteModal.contato.validation.phone')
    return false
  }
  fieldErrors.value.valor = ''
  return true
}

const canSave = computed(() => {
  const v = (local.value.valor || '').trim()
  if (!v) return false
  if (local.value.tipo === 'EMAIL') return emailRegex.test(v)
  if (local.value.tipo === 'TELEFONE' || local.value.tipo === 'WHATSAPP') return v.replace(/\D/g, '').length >= 10
  return true
})

const onTipoChange = (tipo) => {
  local.value.tipo = tipo
  local.value.valor = ''
  fieldErrors.value.valor = ''
}

const onValorInput = (v) => {
  if (local.value.tipo === 'TELEFONE' || local.value.tipo === 'WHATSAPP') {
    local.value.valor = maskPhoneBr(v)
  } else {
    local.value.valor = v
  }
}

watch(() => props.open, (v) => {
  if (v) {
    local.value = props.initial ? { ...empty(), ...props.initial } : empty()
    fieldErrors.value = { valor: '' }
  }
})

const save = () => {
  if (!validate()) return
  emit('save', { ...local.value })
  emit('close')
}
</script>

<style scoped>
.cliente-submodal {
  border-radius: 4px;
  overflow: hidden;
}
.cliente-submodal__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-primary);
}
.cliente-submodal__body {
  padding: 1rem 1.25rem;
}
.cliente-submodal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
}
</style>
